import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, TreePine, Users, Calendar, ArrowRight, Heart, ExternalLink } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import axios from 'axios';
import { selectCountyUpcomingDisplayEvents } from '../utils/eventsSource';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countyData = {
  rutland: {
    name: 'Rutland',
    description: "England's smallest county, home to Rutland Water and rolling hills. Our work here focuses on protecting the unique character of market towns and villages, preserving dark skies, and maintaining the landscape around Rutland Water.",
    heroImage: '/images/cpre images for website/20.png',
    mapBbox: '-0.85,52.55,-0.45,52.78',
    stats: { projects: 12, volunteers: 85, events: 6, yearsActive: 89 },
    highlights: [
      'Rutland Water Nature Reserve protection',
      'Dark skies preservation campaign',
      'Historic village conservation',
      'Footpath maintenance programs'
    ],
    keyProjects: [
      { title: 'Rutland Water Habitat Restoration', status: 'ongoing', category: 'habitat' },
      { title: 'Oakham Town Trail', status: 'completed', category: 'trails' },
      { title: 'Dark Skies Initiative', status: 'ongoing', category: 'environmental' }
    ]
  },
  lincolnshire: {
    name: 'Lincolnshire',
    description: "Lincolnshire, England’s second-largest county, is far more diverse than its reputation suggests. From the vast Fenlands in the south to the rolling chalk hills of The Wolds, the county boasts striking landscapes and a rich history of Roman settlements, Viking battles, and medieval monasteries. Today, however, this unique heritage faces growing threats from large-scale developments including pylons, solar farms, housing expansion, and AI data centres.",
    heroImage: '/images/cpre images for website/11.jpg',
    mapBbox: '-0.8,52.7,0.35,53.6',
    stats: { projects: 18, volunteers: 120, events: 8, yearsActive: 92 },
    highlights: [
      'Protection from overhead pylon lines and substations',
      'Campaigning against mass solar farms on agricultural land',
      'Rooftop and brownfield solar as the alternative',
      'Lincoln Cathedral viewpoint protection'
    ],
    keyProjects: [
      { id: '5', title: 'Opposing Overhead Pylon Infrastructure', status: 'ongoing', category: 'planning' },
      { id: '6', title: 'Solar Farm Agricultural Land Protection', status: 'ongoing', category: 'planning' },
      { id: '7', title: 'Lincolnshire Wolds Landscape Preservation', status: 'ongoing', category: 'habitat' },
    ]
  }
};

const CountyPage = () => {
  const { county } = useParams();
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const data = countyData[county?.toLowerCase()];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, eventsRes] = await Promise.all([
          axios.get(`${API}/projects`, { params: { county: data?.name } }),
          axios.get(`${API}/events`, { params: { upcoming_only: true, limit: 50 } })
        ]);
        setProjects(projectsRes.data.slice(0, 6));
        setEvents(selectCountyUpcomingDisplayEvents(eventsRes.data || []));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (data) fetchData();
  }, [county, data]);

  if (!data) {
    return (
      <Layout>
        <div className="section-padding text-center">
          <h2>County not found</h2>
          <p className="text-muted-foreground mb-4">Please select a valid county.</p>
          <Link to="/initiatives">
            <Button>View All Initiatives</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid={`county-page-${county}`}>
        {/* Hero Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={data.heroImage}
              alt={data.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cpre-new-yew/95 via-cpre-new-yew/80 to-cpre-new-yew/40" />
          </div>
          <div className="relative container-custom">
            <Breadcrumbs
              variant="light"
              customItems={[
                { label: 'Countryside Initiatives', href: '/initiatives' },
                { label: data.name },
              ]}
            />
            <div className="max-w-3xl text-white mt-8">
              <Badge
                variant="outline"
                className="border-transparent bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-4"
              >
                <MapPin className="w-4 h-4 mr-1" />
                {data.name}
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 text-white">
                CPRE {data.name}
              </h1>
              <p className="text-xl text-white/90 leading-relaxed mb-8">
                {data.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/volunteering">
                  <Button size="lg" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-8">
                    <Users className="w-5 h-5 mr-2" />
                    Volunteer in {data.name}
                  </Button>
                </Link>
                <Link to={`/projects?county=${data.name}`}>
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8">
                    View Projects
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics */}
        <section className="py-12 bg-white border-b border-stone-200">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-new-yew">{data.stats.projects}</span>
                <span className="text-muted-foreground">Active Projects</span>
              </div>
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-autumn-leaf">{data.stats.volunteers}</span>
                <span className="text-muted-foreground">Volunteers</span>
              </div>
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-hill">{data.stats.events}</span>
                <span className="text-muted-foreground">Events This Year</span>
              </div>
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-harvest-gold">{data.stats.yearsActive}</span>
                <span className="text-muted-foreground">Years Active</span>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="section-padding bg-cpre-pebble/20">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-cpre-autumn-leaf font-medium text-sm uppercase tracking-wider">Our Area</span>
                <h2 className="mt-2 mb-6">{data.name} at a Glance</h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Explore our conservation work across {data.name}. Click on the map to see project locations and learn about our initiatives in your local area.
                </p>
                <h4 className="font-semibold mb-4">Key Focus Areas:</h4>
                <ul className="space-y-3 mb-8">
                  {data.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-cpre-new-yew/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <TreePine className="w-3 h-3 text-cpre-new-yew" />
                      </div>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
                <Link to={`/projects?county=${data.name}`}>
                  <Button className="bg-cpre-new-yew hover:bg-cpre-hill text-white rounded-full">
                    View All {data.name} Projects
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-stone-200 shadow-lg">
                <iframe
                  title={`${data.name} Map`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${data.mapBbox}&layer=mapnik`}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Key Projects */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-cpre-autumn-leaf font-medium text-sm uppercase tracking-wider">Current Work</span>
              <h2 className="mt-2">Key Projects in {data.name}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {data.keyProjects.map((project, index) => (
                <Card key={index} className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300 group">
                  <CardContent className="p-6">
                    <Badge className={`mb-4 ${
                      project.status === 'ongoing' ? 'bg-cpre-hill text-white' : 'bg-cpre-new-yew text-white'
                    }`}>
                      {project.status === 'ongoing' ? 'Ongoing' : 'Completed'}
                    </Badge>
                    <h3 className="font-heading font-semibold text-xl mb-3">{project.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4 capitalize">
                      Category: {project.category.replace('_', ' ')}
                    </p>
                    <Link to={`/projects/${project.id}`} className="inline-flex items-center text-cpre-new-yew font-medium text-sm group-hover:gap-3 gap-2 transition-all duration-200">
                      Learn more
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to={`/projects?county=${data.name}`}>
                <Button variant="outline" className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full px-8">
                  View All Projects in {data.name}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Events Section */}
        <section className="section-padding bg-cpre-pebble/30">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-cpre-autumn-leaf font-medium text-sm uppercase tracking-wider">Get Involved</span>
              <h2 className="mt-2">Upcoming Events in {data.name}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {events.map((event) => {
                const eventDate = new Date(event.event_date);
                const monthLabel = eventDate.toLocaleDateString('en-GB', {
                  month: 'long',
                  year: 'numeric',
                });
                return (
                  <Card key={event.id} className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-cpre-harvest-gold/20 rounded-xl flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-cpre-harvest-gold" />
                        </div>
                        <div>
                          <p className="font-medium">{monthLabel}</p>
                        </div>
                      </div>
                      <h3 className="font-heading font-semibold text-lg mb-2">
                        {event.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {event.description || `Join us for activities across ${data.name}.`}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link to="/events">
                <Button className="bg-cpre-new-yew hover:bg-cpre-hill text-white rounded-full px-8">
                  View All Events
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-cpre-new-yew text-white">
          <div className="container-custom text-center">
            <Heart className="w-16 h-16 mx-auto mb-6 text-cpre-autumn-leaf" />
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4 text-white">
              Support CPRE {data.name}
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Help us protect the beautiful countryside of {data.name} for future generations.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/donate">
                <Button size="lg" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-8">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate
                </Button>
              </Link>
              <Link to="/volunteering">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CountyPage;
